/** Visible viewport metrics for mobile fullscreen/sheet dialogs (browser chrome safe). */
export interface VisualViewportMetrics {
	height: number;
	top: number;
	bottomInset: number;
}

/**
 * iOS WebKit (Safari and other iOS browsers) — dynamic URL/tool bars break vh/dvh
 * and can make fixed dialogs taller than the visible screen.
 */
export function needsIOSViewportWorkaround(): boolean {
	if (typeof navigator === 'undefined') {
		return false;
	}

	const ua = navigator.userAgent;
	return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/**
 * Prefer the *small* viewport so dialogs are never taller than the area available
 * when browser chrome is visible. Shrink further when visualViewport is smaller
 * (e.g. on-screen keyboard).
 */
export function getVisualViewportMetrics(): VisualViewportMetrics {
	if (typeof window === 'undefined') {
		return { height: 0, top: 0, bottomInset: 0 };
	}

	const visualViewport = window.visualViewport;
	const layoutHeight = window.innerHeight;
	const vvHeight = visualViewport?.height ?? layoutHeight;
	const vvTop = visualViewport?.offsetTop ?? 0;
	const smallViewportHeight = measureSmallViewportHeight(layoutHeight);

	// iOS: never trust the large viewport after chrome collapse — always cap to svh,
	// and after body-lock prefer the smaller of svh / innerHeight.
	const height = needsIOSViewportWorkaround()
		? Math.round(Math.min(smallViewportHeight, layoutHeight, vvHeight))
		: Math.round(Math.min(vvHeight, smallViewportHeight, layoutHeight));

	const top = Math.round(needsIOSViewportWorkaround() ? 0 : vvTop);
	const bottomInset = Math.round(Math.max(0, layoutHeight - vvTop - vvHeight));

	return { height, top, bottomInset };
}

/** CSS height string that is stable on iOS Safari (does not use large viewport). */
export function getStableViewportHeightCss(reservedTop = 0, margin = 0): string {
	const offset = reservedTop + margin;
	return offset > 0 ? `calc(100svh - ${offset}px)` : '100svh';
}

/** Measure 100svh in px (fallback to innerHeight when svh unsupported). */
export function measureSmallViewportHeight(fallback = typeof window !== 'undefined' ? window.innerHeight : 0): number {
	if (typeof document === 'undefined') {
		return fallback;
	}

	const probe = document.createElement('div');
	probe.style.cssText =
		'position:fixed;inset:0 auto auto 0;height:100svh;width:0;visibility:hidden;pointer-events:none;z-index:-1';
	document.documentElement.appendChild(probe);
	const measured = probe.getBoundingClientRect().height || probe.offsetHeight;
	probe.remove();

	return measured > 0 ? Math.round(measured) : fallback;
}

/** Subscribe to visualViewport / window size changes. Returns unsubscribe. */
export function bindVisualViewportChange(callback: () => void): () => void {
	if (typeof window === 'undefined') {
		return () => undefined;
	}

	const onChange = (): void => {
		callback();
	};

	window.visualViewport?.addEventListener('resize', onChange);
	window.visualViewport?.addEventListener('scroll', onChange);
	window.addEventListener('resize', onChange);
	window.addEventListener('orientationchange', onChange);

	return () => {
		window.visualViewport?.removeEventListener('resize', onChange);
		window.visualViewport?.removeEventListener('scroll', onChange);
		window.removeEventListener('resize', onChange);
		window.removeEventListener('orientationchange', onChange);
	};
}

/** Freeze document scroll while a mobile dialog is open (critical for iOS Safari fixed overlays). */
export function lockBodyScroll(): () => void {
	if (typeof document === 'undefined') {
		return () => undefined;
	}

	const { body, documentElement } = document;
	const scrollY = window.scrollY;
	const previous = {
		position: body.style.position,
		top: body.style.top,
		left: body.style.left,
		right: body.style.right,
		width: body.style.width,
		overflow: body.style.overflow,
		htmlOverflow: documentElement.style.overflow,
	};

	body.style.position = 'fixed';
	body.style.top = `-${scrollY}px`;
	body.style.left = '0';
	body.style.right = '0';
	body.style.width = '100%';
	body.style.overflow = 'hidden';
	documentElement.style.overflow = 'hidden';

	return () => {
		body.style.position = previous.position;
		body.style.top = previous.top;
		body.style.left = previous.left;
		body.style.right = previous.right;
		body.style.width = previous.width;
		body.style.overflow = previous.overflow;
		documentElement.style.overflow = previous.htmlOverflow;
		window.scrollTo(0, scrollY);
	};
}
